import {
	AppstoreOutlined,
	CheckCircleOutlined,
	ContainerOutlined,
	HomeOutlined,
	LeftOutlined,
	ShoppingCartOutlined,
} from "@ant-design/icons";
import { Button, Select, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { blocksApi } from "@/modules/objects/api/blocks.api";
import { buildingsApi } from "@/modules/objects/api/buildings.api";
import type { Block, Building } from "@/modules/objects/types/objects.types";
import { EmptyState } from "@/shared/components/EmptyState";
import { getRoomsReport, type RoomsReportData } from "../services/rooms.api";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatNum(n: number) {
	return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

// ─── Bar chart ────────────────────────────────────────────────────────────────

type FloorStat = {
	floor: number;
	total: number;
	sold: number;
};

function FloorBarChart({ floors }: { floors: FloorStat[] }) {
	const maxTotal = Math.max(...floors.map((f) => f.total), 1);
	const chartH = 180;

	return (
		<div className="crm-rooms-bar-wrap">
			{floors.map((f) => {
				const totalH = Math.max(8, (f.total / maxTotal) * chartH);
				const soldH = Math.max(0, (f.sold / maxTotal) * chartH);
				return (
					<div className="crm-rooms-bar-col" key={f.floor}>
						<div className="crm-rooms-bar-track" style={{ height: chartH }}>
							<div className="crm-rooms-bar-bg" style={{ height: totalH }} />
							<div className="crm-rooms-bar-sold" style={{ height: soldH }} />
						</div>
						<span className="crm-rooms-bar-label">{f.floor}</span>
					</div>
				);
			})}
		</div>
	);
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function RoomsReportPage() {
	const navigate = useNavigate();
	const location = useLocation();

	const [buildings, setBuildings] = useState<Building[]>([]);
	const [blocks, setBlocks] = useState<Block[]>([]);
	const [buildingId, setBuildingId] = useState<string | undefined>();
	const [blockId, setBlockId] = useState<string>("all");
	const [report, setReport] = useState<RoomsReportData | null>(null);

	const [loading, setLoading] = useState<boolean>(true);
	const [metaLoading, setMetaLoading] = useState<boolean>(true);

	const fromDashboard = (location.state as any)?.fromDashboard === true;

	// Load initial buildings list
	useEffect(() => {
		async function loadInitialData() {
			try {
				setMetaLoading(true);
				const bldgs = await buildingsApi.getBuildings({ limit: 100, isActive: "true" });
				setBuildings(bldgs);
				if (bldgs.length > 0) {
					setBuildingId(bldgs[0].id);
				} else {
					setLoading(false);
				}
			} catch (error) {
				console.error("Failed to load buildings list:", error);
			} finally {
				setMetaLoading(false);
			}
		}
		loadInitialData();
	}, []);

	// Fetch blocks when selected building changes
	useEffect(() => {
		if (!buildingId) {
			return;
		}
		async function loadBlocks() {
			try {
				const blks = await blocksApi.getBlocks({ buildingId });
				setBlocks(blks);
			} catch (error) {
				console.error("Failed to load blocks list:", error);
			}
		}
		loadBlocks();
	}, [buildingId]);

	// Fetch rooms report data when filters change
	useEffect(() => {
		if (!buildingId) {
			return;
		}
		async function loadReport() {
			try {
				setLoading(true);
				const data = await getRoomsReport({ buildingId, blockId });
				setReport(data);
			} catch (error) {
				console.error("Failed to load rooms report:", error);
			} finally {
				setLoading(false);
			}
		}
		loadReport();
	}, [buildingId, blockId]);

	const buildingOptions = useMemo(
		() => buildings.map((b) => ({ label: b.name, value: b.id })),
		[buildings]
	);

	const blockOptions = useMemo(
		() => [
			{ label: "Barcha blok", value: "all" },
			...blocks.map((bl) => ({ label: bl.name, value: bl.id })),
		],
		[blocks]
	);

	const handleBuildingChange = (val: string) => {
		setBuildingId(val);
		setBlockId("all");
	};

	if (metaLoading) {
		return (
			<div className="crm-rooms-report-loading" style={{ padding: 40, textAlign: "center" }}>
				<Spin size="large" tip="Binolar yuklanmoqda..." />
			</div>
		);
	}

	if (buildings.length === 0) {
		return (
			<div style={{ padding: 40 }}>
				<EmptyState
					icon={<ContainerOutlined style={{ fontSize: 48, color: "#ccc" }} />}
					title="Bino topilmadi"
					description="Tizimda hisobot yaratish uchun faol binolar mavjud emas."
				/>
			</div>
		);
	}

	const total = report?.total ?? 0;
	const sold = report?.sold ?? 0;
	const reserved = report?.reserved ?? 0;
	const free = report?.available ?? 0;
	const floors = report?.floors ?? [];
	const byType = report?.byType ?? [];

	return (
		<div className="crm-rooms-report">
			{/* ── Header ── */}
			<div className="crm-page-head">
				<div>
					<div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
						{fromDashboard && (
							<Button icon={<LeftOutlined />} onClick={() => navigate("/dashboard")} type="text" />
						)}
						<h1>Xonalar holati hisoboti</h1>
					</div>
					<p></p>
				</div>
				<div className="crm-report-actions">
					<Select
						value={buildingId}
						onChange={handleBuildingChange}
						options={buildingOptions}
						className="w-full md:w-[180px]"
					/>
					<Select
						value={blockId}
						onChange={setBlockId}
						options={blockOptions}
						className="w-full md:w-[150px]"
					/>
				</div>
			</div>

			{loading ? (
				<div className="crm-rooms-report-loading" style={{ padding: 100, textAlign: "center" }}>
					<Spin size="large" tip="Hisobot yuklanmoqda..." />
				</div>
			) : (
				<>
					{/* ── KPI ── */}
					<section className="crm-report-kpis">
						<div
							className="crm-kpi crm-report-kpi"
							style={{ cursor: "pointer" }}
							onClick={() =>
								navigate(
									`/rooms?buildingId=${buildingId}${blockId === "all" ? "" : `&blockId=${blockId}`}`,
									{ state: { returnTo: location.pathname } }
								)
							}
						>
							<div className="crm-kpi-title">
								<span className="crm-kpi-icon blue">
									<AppstoreOutlined />
								</span>
								Jami
							</div>
							<div className="crm-kpi-value">
								<strong>{formatNum(total)}</strong>
							</div>
							<div className="crm-kpi-note">barcha xonalar</div>
						</div>

						<div
							className="crm-kpi crm-report-kpi"
							style={{ cursor: "pointer" }}
							onClick={() =>
								navigate(
									`/rooms?buildingId=${buildingId}${blockId === "all" ? "" : `&blockId=${blockId}`}&status=sotilgan`,
									{ state: { returnTo: location.pathname } }
								)
							}
						>
							<div className="crm-kpi-title">
								<span className="crm-kpi-icon green">
									<ShoppingCartOutlined />
								</span>
								Sotilgan
							</div>
							<div className="crm-kpi-value">
								<strong>{formatNum(sold)}</strong>
							</div>
							<div className="crm-kpi-note">
								{total > 0 ? Math.round((sold / total) * 100) : 0}% band
							</div>
						</div>

						<div
							className="crm-kpi crm-report-kpi"
							style={{ cursor: "pointer" }}
							onClick={() =>
								navigate(
									`/rooms?buildingId=${buildingId}${blockId === "all" ? "" : `&blockId=${blockId}`}&status=sotuvda`,
									{ state: { returnTo: location.pathname } }
								)
							}
						>
							<div className="crm-kpi-title">
								<span className="crm-kpi-icon sky">
									<HomeOutlined />
								</span>
								Bo'sh
							</div>
							<div className="crm-kpi-value">
								<strong>{formatNum(free)}</strong>
							</div>
							<div className="crm-kpi-note">sotishga tayyor</div>
						</div>

						<div
							className="crm-kpi crm-report-kpi"
							style={{ cursor: "pointer" }}
							onClick={() =>
								navigate(
									`/rooms?buildingId=${buildingId}${blockId === "all" ? "" : `&blockId=${blockId}`}&status=band`,
									{ state: { returnTo: location.pathname } }
								)
							}
						>
							<div className="crm-kpi-title">
								<span className="crm-kpi-icon amber">
									<CheckCircleOutlined />
								</span>
								Band
							</div>
							<div className="crm-kpi-value">
								<strong>{formatNum(reserved)}</strong>
							</div>
							<div className="crm-kpi-note">bron qilingan</div>
						</div>
					</section>

					{/* ── Charts grid ── */}
					<div className="crm-report-grid" style={{ marginTop: 14 }}>
						{/* Qavat bo'yicha bar chart */}
						<div className="crm-card crm-report-chart-card">
							<div className="crm-card-head">
								<h3>Qavat bo'yicha</h3>
							</div>
							<div style={{ padding: "18px 20px 16px" }}>
								{floors.length > 0 ? (
									<FloorBarChart floors={floors} />
								) : (
									<div style={{ padding: 40, textAlign: "center", color: "#999" }}>
										Ma'lumotlar mavjud emas
									</div>
								)}
							</div>
						</div>

						{/* Xona soni bo'yicha table */}
						<div className="crm-card crm-report-table-card">
							<div className="crm-card-head">
								<h3>Xona soni bo'yicha</h3>
							</div>
							<div className="crm-table-wrap">
								{byType.length > 0 ? (
									<table className="crm-table crm-rooms-type-table">
										<thead>
											<tr>
												<th>Tur</th>
												<th style={{ textAlign: "right" }}>Jami</th>
												<th style={{ textAlign: "right" }}>Sotilgan</th>
												<th>%</th>
											</tr>
										</thead>
										<tbody>
											{byType.map((row) => {
												const pct = row.total > 0 ? Math.round((row.sold / row.total) * 100) : 0;
												const roomsCount = row.label.split("-")[0];
												return (
													<tr
														key={row.label}
														style={{ cursor: "pointer" }}
														className="crm-table-row-hover"
														onClick={() =>
															navigate(
																`/rooms?buildingId=${buildingId}${blockId === "all" ? "" : `&blockId=${blockId}`}&roomsCount=${roomsCount}`,
																{ state: { returnTo: location.pathname } }
															)
														}
													>
														<td>
															<strong>{row.label}</strong>
														</td>
														<td style={{ textAlign: "right" }}>{row.total}</td>
														<td style={{ textAlign: "right" }}>{row.sold}</td>
														<td>
															<div className="crm-rooms-type-bar">
																<div
																	className="crm-rooms-type-bar-fill"
																	style={{ width: `${pct}%` }}
																/>
															</div>
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								) : (
									<div style={{ padding: 40, textAlign: "center", color: "#999" }}>
										Ma'lumotlar mavjud emas
									</div>
								)}
							</div>
						</div>
					</div>
				</>
			)}
		</div>
	);
}

export default RoomsReportPage;
